import ksBridge from '@ks/ks-bridge';
import yoda from '@ks/yoda-js-sdk';
import Toast from '@components/Toast';
import { IVideos } from '@schemas/video';
import { PRODUCT_TYPE, ORDER_STATUS, QUERY_PRODTYPE } from '@schemas/order';
import { getCookie } from '@utils/cookies';
import { IM_COMMON_CONFIG, RESPONSE_SUCCESS_CODE } from '@/configs/constants';
import { debugConsole } from '@aio/utils';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import weblog, { sendTask } from '../../../../libs/weblog/src';
import { getTargetUrl } from './url';

export const { isInYoda } = yoda;

export const transFeedVideos = (videos: IVideos) => {
  if (!videos) {
    return [[], []];
  }
  let i = 0;
  let heightLeft = 0;
  let heightRight = 0;
  const left: IVideos = [];
  const right: IVideos = [];
  while (i < videos.length) {
    const coverHeight = videos[i].height;
    if (heightLeft <= heightRight) {
      left.push({
        ...videos[i],
        index: i
      });
      heightLeft += coverHeight;
    } else {
      right.push({
        ...videos[i],
        index: i
      });
      heightRight += coverHeight;
    }
    i += 1;
  }
  return [left, right];
};

/**
 * 计算距离目标时间剩余XX小时XX分
 * @param time eg:'2020-11-04 11:20:43'
 */
export function getRemainTime(time: string | undefined) {
  if (!time) return null;
  let inputTime = time;
  // ios 时间兼容问题
  if (inputTime.includes('-')) {
    inputTime = time.replace(/\.|\-/g, '/');
  }
  const targetTime = new Date(inputTime);
  const now = new Date();
  const gap = targetTime.getTime() - now.getTime();
  if (gap < 0) {
    return null;
  }
  const totalMinutes = +(gap / 1000 / 60).toFixed(0);
  const remainHours = Math.floor(totalMinutes / 60);
  const remainMinutes = totalMinutes - remainHours * 60;

  const showHours = remainHours ? `${remainHours}小时` : '';
  const showMinutes = remainMinutes ? `${remainMinutes}分钟` : '';
  return `${showHours}${showMinutes}`;
}

/**
 * 计算距离起始时间XX小时XX分
 * @param time eg:'2020-11-04 11:20:43' → XX小时前
 *  <1min: 刚刚
 *  1min-60min：xx分钟前
 *  1h-24h：xx小时前
 *  > 24h < 1年：若还在本年，则显示xx-xx  11-23
 *  > 1年：20xx-xx-xx
 */
export function getBeforeTime(time: string | undefined) {
  if (!time) return null;
  let inputTime = time;
  if (inputTime.includes('-')) {
    inputTime = time.replace(/\.|\-/g, '/');
  }
  const startTime = new Date(inputTime);
  const now = new Date();
  const gap = now.getTime() - startTime.getTime();
  if (gap < 0) {
    return null;
  }
  const totalMinutes = +(gap / 1000 / 60).toFixed(0);
  const remainHours = Math.floor(totalMinutes / 60);
  // const remainMinutes = totalMinutes - remainHours * 60;
  if (totalMinutes <= 1) {
    return '刚刚';
  }
  if (totalMinutes > 1 && totalMinutes <= 60) {
    return `${totalMinutes}分钟前`;
  }
  if (totalMinutes > 60 && totalMinutes <= 60 * 24) {
    return `${remainHours}小时前`;
  }
  if (totalMinutes > 60 * 24 && startTime.getFullYear() === now.getFullYear()) {
    return `${startTime.getMonth() + 1}-${startTime.getDate()}`;
  }
  // startTime.getFullYear() < now.getFullYear()
  return time.split(' ')[0];
}

/**
 * 判断是否需要跳转登陆
 * @param cb () => void
 */

export function toLogin(
  cb = () => {
    // sucess callback
  },
  fail = () => {
    // fail callback
  }
) {
  if (ksBridge.sniff.isInApp) {
    debugConsole('ksBridge.login');
    ksBridge.login({
      success: cb,
      fail
    });
  }
}

/**
 * 判断是否是医生
 */

export function isSameDoctor(userId, doctorId) {
  if (userId === doctorId) {
    Toast.show('您不可以对自己咨询哦');
    return true;
  }
  return false;
}

/**
 * 获取下单 url
 */
export function getOrderUrl(
  doctorId, // 医生ID
  productType = PRODUCT_TYPE.Message // 问诊类型
) {
  if (
    productType !== PRODUCT_TYPE.Message &&
    productType !== PRODUCT_TYPE.Call
  ) {
    return `/quick-consult/create?prodType=${
      QUERY_PRODTYPE[productType] || `quickConsult`
    }`;
  }
  return `/order/create/${doctorId}/${productType}`;
}
interface IGetRegisterUrlParams {
  hospitalDoctorId: number | string | undefined;
  hospitalIndexId: number | string | undefined;
  hospitalDepartmentId: number | string | undefined;
  orderPrice?: string;
}

/**
 * @param pathUrl 获取挂号订单url
 * @returns url
 * eg: `/order/register-create/${doctorHospitalId}?hospitalIndexId=${selectedHospitalId}&hospitalDepartmentId=${selectedDepartmentId}&selectedDateId=${selectedDateId}&selectedPrice=${selectedPrice}&selectedTimeId=${value.id}`
 */
export function getRegisterOrderUrl({
  hospitalDoctorId = '',
  hospitalIndexId = '',
  hospitalDepartmentId = '',
  orderPrice
}: IGetRegisterUrlParams) {
  let baseUrl = `/order/register-create/${hospitalDoctorId}?hospitalIndexId=${hospitalIndexId}&hospitalDepartmentId=${hospitalDepartmentId}`;
  if (orderPrice) {
    baseUrl = `${baseUrl}&selectedPrice=${orderPrice}`;
  }
  return getTargetUrl(baseUrl);
}

/**
 * 获取下单完整url, IM 点击[再次咨询]跳转时使用
 */

export function getActionUrl(pathUrl) {
  return `${window.location.origin}${pathUrl}&layoutType=4&enableWK=1&hyId=healthy-channel`;
}

/**
 * 是否在站内
 */
export const { isInApp } = ksBridge.sniff;

/**
 * 元素曝光
 * @weblog helper begin
 */

export function sendShow(action, params = {}) {
  if (isInApp) {
    sendTask('SHOW', action, params);
  }
}
/**
 * 页面曝光
 */
export function sendPVShow(action, params = {}) {
  if (isInApp) {
    sendTask('pv', action, params);
  }
}

/**
 * 禁止调用这个方法，会导致页面ID失效
 * 页面曝光应该使用sendPVShow
 */
export function sendPV(action: string, params: Record<string, unknown>) {
  if (isInApp) {
    // 这么调用不会发送页面曝光...
    // logShow('PV', action, params);
    // 更新页面所有携带参数
    weblog.initPageParams(params);
    // 发送PV曝光同 action 的元素曝光
    sendShow(action);
  }
}
export function sendClick(action, params?) {
  if (isInApp) {
    if (params) {
      sendTask('CLICK', action, params);
    } else {
      sendTask('CLICK', action);
    }
  }
}

export function sendClickInWeb(action, params?) {
  if (!isInApp) {
    if (params) {
      sendTask('CLICK', action, params);
    } else {
      sendTask('CLICK', action);
    }
  }
}
/**
 * @weblog helper end
 */

/**
 * 转换数字 1000 -> 1.0W
 */
export function formatNumber(num) {
  if (!num) {
    return '0';
  }

  // const baseK = 1000;
  const baseW = 10000;
  // const baseM = 1000000;

  // if (num >= baseM) {
  //   return `${(num / baseM).toFixed(1)}m`;
  // }

  if (num >= baseW) {
    return `${(num / baseW).toFixed(1)}w`;
  }

  // if (num >= baseK) {
  //   return `${(num / baseK).toFixed(1)}k`;
  // }

  return `${num}`;
}

/**
 * 转换数字 1000 -> 1.0km
 */
export function formatKiometer(num: number | undefined) {
  if (!num) {
    return '';
  }

  if (typeof num !== 'number') {
    return '';
  }

  const baseK = 1000;

  if (num >= baseK) {
    return `${(num / baseK).toFixed(1)}km`;
  }

  if (num >= 0) {
    return `${num.toFixed(0)}m`;
  }

  return `${num}`;
}

/**
 * 防抖
 */
export const debounce = (fn, time = 300) => {
  let timer;
  return function debounceFunction(...arg) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn.call(this, ...arg);
    }, time);
  };
};

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string | null}
 */
export function parseTime(rTime, cFormat) {
  let time = rTime;
  if (arguments.length === 0 || !time) {
    return null;
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}';
  let date;
  if (typeof time === 'object') {
    date = time;
  } else {
    if (typeof time === 'string') {
      if (/^[0-9]+$/.test(time)) {
        // support "1548221490638"
        time = parseInt(time, 10);
      } else {
        // support safari
        // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        time = time.replace(new RegExp(/-/gm), '/');
      }
    }

    if (typeof time === 'number' && time.toString().length === 10) {
      time *= 1000;
    }
    date = new Date(time);
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  };
  const timeStr = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key];
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') {
      return ['日', '一', '二', '三', '四', '五', '六'][value];
    }
    return value.toString().padStart(2, '0');
  });
  return timeStr;
}
export const formatReplyTime = (min: number) => {
  if (!min || min === -1) {
    return '暂无';
  }

  if (min <= 50) {
    let time = Math.ceil(min / 10) * 10;
    time = parseInt(`${time}`, 10);
    return `${time}分钟内`;
  }

  if (min > 50 && min < 60 * 24) {
    const hour = Math.ceil(min / 60);
    return hour < 24 ? `${hour}小时内` : '1天内';
  }

  return `${Math.round(min / (60 * 24))}天内`;
};

export function formatReplyTimeDetail(min: number) {
  if (!min) {
    return '暂无';
  }

  if (min < 60) {
    let time = Math.ceil(min / 10) * 10;
    time = parseInt(`${time}`, 10);
    return `${time}分钟`;
  }

  const hour = Math.ceil(min / 60);
  const minutes = Math.ceil(min % 60);
  return `${hour}小时${minutes}分`;
}

export const debounceOpenShare = debounce((path = '', config = {} as any) => {
  sendClick('SHARE_BUTTON');
  ksBridge.login({
    success: (res) => {
      if (res.result === 1) {
        const sharePath = path || window.location.pathname.substring(1);
        // 唤起分享面板
        ksBridge.share({
          param: {
            subBiz: 'HEALTHY_CHANNEL',
            shareResourceType: getCookie('kpn'), // 'KUAISHOU', 'NEBULA'
            shareObjectId: getCookie('ud'),
            showSharePanel: true,
            commonConfigs: {
              title: config.title || '快手健康',
              subTitle:
                config.subTitle ||
                '快手，国民短视频社区，记录和分享生活的平台。在这里，看到真实有趣的世界，找到自己感兴趣的人，也可以让世界发现真实有趣的自己。快手，记录世界记录你。',
              // path: ''
              // path: 'doctors/{doctorId}'
              path: sharePath
            },
            forceCommonConfigs: {
              coverUrls: [
                'https://s2-10604.kwimgs.com/kos/nlav10604/share-logo-cover.png'
              ]
            },
            tokenStoreParams: {
              path: sharePath,
              shareUrl: sharePath
            }
          },
          fail: (err) => {
            console.log(`fail:${JSON.stringify(err)}`);
          }
        });
      }
    }
  });
});

// 基础 share
export const basicShare = debounce((config = {} as any) => {
  ksBridge.login({
    success: (res) => {
      if (res.result === 1) {
        // 唤起分享面板
        ksBridge.share({
          param: {
            subBiz: config.subBiz || 'HEALTHY_CHANNEL',
            shareResourceType: getCookie('kpn'), // 'KUAISHOU', 'NEBULA'
            shareObjectId: getCookie('ud'),
            showSharePanel: true
          },
          success: (r) => {
            console.log(r);
          },
          fail: (err) => {
            console.log('share-err', err);
          }
        });
      }
    }
  });
});

/**
 * 获取站外通用的ClassName
 */
export const getCls = () => {
  return ksBridge.sniff.isInApp ? '' : 'notInApp';
};

export const isPhoneNumber = (phone) => {
  return /^1\d{10}$/.test(phone);
};

export function isFunction(obj: any) {
  return typeof obj === 'function';
}

function getTwoString(num) {
  const tNum = Number(num);
  if (Number.isNaN(tNum)) return num;
  return tNum < 10 ? `0${tNum}` : `${tNum}`;
}

export const getReserveTime = (timeStr) => {
  // "2021年5月28日08:00~09:00" 转换为 "2021-5-28 08:00～09:00"
  const regex = /(.*)年(.*)月(.*)日(.*)~(.*)/;
  if (regex.test(timeStr)) {
    return timeStr.replace(
      regex,
      (match, $1, $2, $3, $4, $5) =>
        `${$1}-${getTwoString($2)}-${getTwoString($3)} ${$4}～${$5}`
    );
  }
  return timeStr;
};

export const getReserveTimeForCard = (timeStr) => {
  // "2021年5月28日08:00~09:00" 转换为 "5月28日 08:00～09:00"
  const regex = /(.*)年(.*)月(.*)日(.*)~(.*)/;
  if (regex.test(timeStr)) {
    return timeStr.replace(
      regex,
      (match, $1, $2, $3, $4, $5) => `${$2}月${$3}日 ${$4}～${$5}`
    );
  }
  return timeStr;
};

// 身份证号匹配
export const matchIdentityNumber = (idStr: string) => {
  return /^[0-9]{6}([0-9]{9})[0-9]{2}([0-9]|X|x)$/.test(idStr);
};

export const matchPhoneNumber = (phone: string) => {
  return /^1[0-9]{2}(\*{4}|[0-9]{4})[0-9]{4}$/.test(phone);
};

export function checkResponseCode(code) {
  return code === RESPONSE_SUCCESS_CODE;
}

export function checkResponseResult(result) {
  // undefined, null return true
  return result != null;
}

export function checkResponse(code, result) {
  return checkResponseCode(code) && checkResponseResult(result);
}

// 获取挂号订单的就诊时间 'XX 天后就诊'
// eg: "2021-07-01 14:05-14:12"
export function getRegisterTreatTime(timeSlot) {
  if (!timeSlot) return null;
  const time = timeSlot.slice(0, -6);
  let target: {
    tips: string;
    num?: number;
  } = {
    num: 0,
    tips: ''
  };
  let inputTime = time;
  if (inputTime.includes('-')) {
    inputTime = time.replace(/\.|\-/g, '/');
  }
  const targetTime = new Date(inputTime);
  const now = new Date();
  const gap = targetTime.getTime() - now.getTime();
  if (gap < 0) {
    return null;
  }
  const oneDayHours = 1000 * 60 * 60; // 毫秒→小时 一天总共的小时数
  const gapHour = Math.floor(gap / oneDayHours);
  // console.log('---gapHour', gapHour);
  if (gapHour > 24) {
    target = { num: Math.floor(gapHour / 24), tips: '天后就诊' };
    return target;
  }
  if (gapHour >= 1) {
    target = { num: gapHour, tips: '小时后就诊' };
    return target;
  }
  if (gapHour === 0) {
    target = { tips: '即将就诊' };
    return target;
  }
  return null;
}

export function transImageToBase64(imgSrc) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imgSrc;
    image.onload = () => {
      const { width, height } = image;
      canvas.width = width;
      canvas.height = height;
      try {
        context && context.drawImage(image, 0, 0, width, height);
        const result = canvas.toDataURL('image/png');
        resolve(result.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''));
      } catch (err) {
        reject();
      }
    };
    image.onerror = () => {
      reject();
    };
  });
}

export const needShowCountDown = (curStatus: ORDER_STATUS, state?) => {
  if (curStatus === ORDER_STATUS.HasPaid) {
    return true;
  }
  if (curStatus === ORDER_STATUS.Consulting) {
    return true;
  }
  return false;
};

// "2021-07-08" => 7月8日
export const formatRegisterDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  const time = new Date(dateStr);
  const month = time.getMonth() + 1;
  const date = time.getDate();
  const target = `${month}月${date}日`;
  return target;
};

export const toastError = (error) => {
  Toast.show(
    error.message && error.message === '系统错误' ? '网络异常' : error.message
  );
};

export const checkIsNotFromChannel = (key, str) => {
  const querys = { from: ['plc', 'profile', 'sms'], activity: ['skin0922'] };
  return querys?.[key]?.includes(str);
};

export const isJSON = (str) => {
  if (typeof str === 'string') {
    try {
      const obj = JSON.parse(str);
      if (typeof obj === 'object' && obj !== null) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }
  return false;
};

export const getConditionDescription = (str) => {
  if (isJSON(str)) {
    const descriptionList = JSON.parse(str as string);
    const strArr = descriptionList?.map((item, index) => {
      return `${item.answerTitle}：${item.answer}`;
    });
    return strArr?.join('；');
  }

  return str;
};

/**
 * 根据身份证号获取年龄
 * 15位身份证号码：第7、8位为出生年份(两位数)，第9、10位为出生月份，第11、12位代表出生日
 * 18位身份证号码：第7、8、9、10位为出生年份(四位数)，第11、第12位为出生月份，
 * 第13、14位代表出生日期，第17位代表性别，奇数为男，偶数为女。
 */
export const getAgeFromIDCard = (identityCard: string) => {
  const len = String(identityCard).length;
  let strBirthday = '';
  if (len === 18) {
    // 处理18位的身份证号码从号码中得到生日和性别代码
    strBirthday = `${identityCard.substr(6, 4)}/${identityCard.substr(
      10,
      2
    )}/${identityCard.substr(12, 2)}`;
  }
  if (len === 15) {
    let birthdayValue = '';
    birthdayValue = identityCard.charAt(6) + identityCard.charAt(7);
    if (parseInt(birthdayValue, 10) < 10) {
      strBirthday = `20${identityCard.substr(6, 2)}/${identityCard.substr(
        8,
        2
      )}/${identityCard.substr(10, 2)}`;
    } else {
      strBirthday = `19${identityCard.substr(6, 2)}/${identityCard.substr(
        8,
        2
      )}/${identityCard.substr(10, 2)}`;
    }
  }

  if (!strBirthday) {
    return '';
  }

  // 时间字符串里，必须是“/”
  const birthDate = new Date(strBirthday);
  const nowDateTime = new Date();
  const age = nowDateTime.getFullYear() - birthDate.getFullYear();
  // 再考虑月、天的因素;.getMonth()获取的是从0开始的，这里进行比较，不需要加1
  if (
    nowDateTime.getMonth() < birthDate.getMonth() ||
    (nowDateTime.getMonth() === birthDate.getMonth() &&
      nowDateTime.getDate() < birthDate.getDate())
  ) {
    return age - 1;
  }
  return age;
};

// 优惠券时间截取
export const timeInterception = (timeStr) => {
  return timeStr?.split(' ')?.[0]?.replace(/-/g, '.') || '--';
};

export const className = (...classNames: string[]) => classNames.join(' ');

/**
 * 获取健康频道 sid Token
 */
export const getSidToken = () =>
  new Promise((resolve, reject) => {
    ksBridge.getSidToken({
      sid: IM_COMMON_CONFIG.sid,
      forceRefresh: true,
      success: (ret) => {
        if (ret.result === 1) {
          resolve(ret.token);
        } else {
          reject(new Error(ret.message || ret.result));
        }
      },
      fail: (err) => {
        reject(err);
      }
    });
  });

// 获取最低价格，用于展示医生信息xx起
export const getMinPrice = (serviceTypeList) => {
  // console.log({ serviceTypeList });
  const priceList = serviceTypeList
    ?.map((item) =>
      item?.discount ? Number(item.discount) : Number(item.serviceFee)
    )
    ?.sort((a, b) => a - b);
  // console.log({ priceList });
  return priceList?.[0];
};

export const isSamePatient = (p1, p2) => {
  return (
    p1?.patientName === p2?.patientName &&
    p1?.patientGender === p2?.patientGender &&
    p1?.patientAge === p2?.patientAge
  );
};

/**
 * 计算距离起始时间 XX小时XX分
 * @param time 时间戳
 *  今天：上午11：10
 *  昨天： 昨天
 *  更早：显示2021/11/11
 */
export function getLatestTime(time: number) {
  if (!time) return null;
  const today = new Date(new Date().toLocaleDateString());
  // 今天0点时间戳
  const todayTime = today.getTime();
  // 设置成昨天0点
  today.setTime(todayTime - 3600 * 24 * 1000 * 1);
  // 昨天0点时间戳
  const yesterdayTime = today.getTime();
  const now = new Date();
  const gap = now.getTime() - todayTime;
  const targetTime = new Date(time);
  if (gap < 0) {
    return null;
  }
  if (time >= todayTime) {
    return `${
      targetTime.getHours() < 12 ? '上午' : '下午'
    }${targetTime
      .getHours()
      ?.toString()
      ?.padStart(2, '0')}:${targetTime
      .getMinutes()
      ?.toString()
      ?.padStart(2, '0')}`;
  }
  if (time < todayTime && time >= yesterdayTime) {
    return '昨天';
  }
  return `${targetTime.getFullYear()}/${
    targetTime.getMonth() + 1
  }/${targetTime.getDate()}`;
}
export const isValidPatientInfo = (patientVo) => {
  if (typeof patientVo?.patientAge !== 'number') {
    return false;
  }
  if (typeof patientVo?.patientGender !== 'number') {
    return false;
  }
  return true;
};

/**
 *
 * @example
 * word: 皮肤科，keyword: 皮肤
 * return ['皮肤','科'];
 *
 * result使用方式示例
 * result.map((text,index)=>(
 *    <span
 *      style={{color: text===keyword? "自定义高亮颜色" : "默认颜色"}}
 *      key={index}
 *    >
 *      {text}
 *    </span>
 * ))
 *
 * @param word 全部文本
 * @param keyword 关键词高亮文本
 * @returns 返回一个按照关键词分割的数组
 */
export const getHighLightText = (word: string, keyword: string) => {
  const regExp = new RegExp(`(${keyword})`, 'i');
  return word.split(regExp).filter((v) => v);
};

export const getEnv = () => {
  const env = process.env.NX_APP_ENV;
  switch (env) {
    case 'dev':
      return 'testing'; // 开发测试环境
    case 'staging':
      return 'staging'; // 开发测试环境
    case 'preonline':
      return 'preonline'; // 预发环境
    case 'prod':
      return 'production'; // 生产环境
    default:
      return 'production';
  }
};

export function getAdaptiveCss(width: number) {
  return typeof width !== 'number' ? 0 : width * (window.innerWidth / 414);
}

export function getAdaptiveCssPy(height: number) {
  return typeof height !== 'number' ? 0 : height * (window.innerHeight / 896);
}

export function timeFormatter(timeMs: number) {
  const minutes = Math.floor(timeMs / 60);
  const seconds = Math.floor(timeMs % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
}
